import { Box, Button, Checkbox, Chip, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { Fragment } from "react";
import { Link } from "react-router-dom";
import { cpfMask } from "utils/cpf";

import { ContractView, QueueRow, Retention } from "./monitor.types";
import { canResend, ddmm, decisionText, MAX_MANUAL_RESENDS_PER_DAY, pendingPeople, reasonText, rowKey } from "./monitor.utils";

export type MonitorAction = { kind: "RESEND" | "DECIDE" | "CLOSE"; rows: QueueRow[] };

/** why: texto da spec §5.4; "retido" aqui é o SKIPED (vontade humana), nunca a espera da campanha. */
const retentionText = (r: Retention) => `Movimentação retida por ${r.by}${r.reason ? ` (${r.reason})` : ""}`;

/** why: botão desabilitado do MUI não mostra `title` (pointer-events: none); o porquê vai em texto ao lado. */
function resendBlocked(r: QueueRow): string | null {
  if (canResend(r)) return null;
  if (!r.primaryOccurrenceKey) return "sem ocorrência aberta";
  // why: destino não lido não é falta de canal; dizer "sem canal" mandaria o RH procurar um e-mail que pode existir (R8).
  if (r.recipientSource === "ERROR") return "destino não lido";
  if (r.withoutChannel || !r.email) return "sem canal";
  return `limite de ${MAX_MANUAL_RESENDS_PER_DAY} por dia atingido`;
}

export interface MonitorQueueProps {
  rows: QueueRow[];
  contracts: ContractView[];
  showContract: boolean;
  expanded: string | null;
  selected: string[];
  onExpand: (key: string | null) => void;
  onToggle: (key: string) => void;
  onAction: (action: MonitorAction) => void;
}

function emptyText(contracts: ContractView[]): string {
  const unread = contracts.filter((c) => c.unreadable).length;
  if (!unread) return "Ninguém aguardando com estes filtros.";
  /** invariant: contrato não lido não é fila vazia. */
  if (unread === contracts.length) return `Fila indisponível: dados de ${unread} contrato(s) não lidos.`;
  return `Ninguém aguardando nos contratos lidos com estes filtros; dados de ${unread} contrato(s) não lidos.`;
}

function Channel({ r, editDataPath }: { r: QueueRow; editDataPath: string | null }) {
  return (
    <>
      {r.email ?? (r.recipientSource === "ERROR" ? "Não foi possível ler o destino" : "-")}
      {r.withoutChannelReason && <Chip size="small" color="warning" label="Sem canal" sx={{ ml: r.email ? 1 : 0 }} />}
      {r.withoutChannelReason === "SEM_EMAIL" && editDataPath && (
        <Button size="small" component={Link} to={`${editDataPath}?cpf=${r.taxPayerId}`}>Corrigir e-mail</Button>
      )}
      {r.withoutChannelReason === "CORRETORA_DESLIGADA" && (
        <Typography variant="caption" display="block">Comunicação da corretora desligada</Typography>
      )}
      {r.sendError && <Typography variant="caption" display="block" color="error">{r.sendError}</Typography>}
    </>
  );
}

export function MonitorQueue({ rows, contracts, showContract, expanded, selected, onExpand, onToggle, onAction }: MonitorQueueProps) {
  const contractOf = (ws: string) => contracts.find((c) => c.workspaceId === ws);
  if (!rows.length) return <Typography variant="body2">{emptyText(contracts)}</Typography>;
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox" aria-label="Seleção" />
          <TableCell>Nome</TableCell>
          <TableCell>CPF</TableCell>
          {showContract && <TableCell>Contrato</TableCell>}
          <TableCell>Telefone</TableCell>
          <TableCell>E-mail</TableCell>
          <TableCell>Admissão</TableCell>
          <TableCell>Dias na campanha</TableCell>
          <TableCell>Passo</TableCell>
          <TableCell>Último envio</TableCell>
          <TableCell>Vidas decididas</TableCell>
          <TableCell>Motivo</TableCell>
          <TableCell>Ações</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r) => {
          const key = rowKey(r);
          const open = expanded === key;
          const pending = pendingPeople(r);
          const blocked = resendBlocked(r);
          return (
            <Fragment key={key}>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox checked={selected.includes(key)} onChange={() => onToggle(key)} inputProps={{ "aria-label": `Selecionar ${r.name}` }} />
                </TableCell>
                <TableCell>
                  {r.name}
                  {r.outsideSnapshot && <Chip size="small" label="Fora da massa recente" sx={{ ml: 1 }} />}
                  {r.sendsStopped && <Chip size="small" label="Envios encerrados" sx={{ ml: 1 }} />}
                  {r.retention && <Typography variant="caption" display="block">{retentionText(r.retention)}</Typography>}
                </TableCell>
                <TableCell>{cpfMask(r.taxPayerId)}</TableCell>
                {showContract && <TableCell>{contractOf(r.workspaceId)?.label ?? r.workspaceId}</TableCell>}
                <TableCell>{r.phone ?? "-"}</TableCell>
                <TableCell>
                  <Channel r={r} editDataPath={contractOf(r.workspaceId)?.editDataPath ?? null} />
                </TableCell>
                {/* why: a massa manda só o dia; a reserva do payload (fora da massa) vem ISO com hora. */}
                <TableCell>{r.hiringDate ? r.hiringDate.slice(0, 10).split("-").reverse().join("/") : "-"}</TableCell>
                <TableCell>{r.daysInCampaign ?? "-"}</TableCell>
                <TableCell>{r.step !== null ? `${r.step} (${r.sendsInStep ?? 0} envios)` : "-"}</TableCell>
                <TableCell>{r.lastSentAt ? ddmm(r.lastSentAt) : "-"}</TableCell>
                <TableCell>{`${r.lives.decided} de ${r.lives.total}`}</TableCell>
                <TableCell>{reasonText(r.reason)}</TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                    <Button size="small" aria-expanded={open} onClick={() => onExpand(open ? null : key)}>Ver família</Button>
                    <Button
                      size="small"
                      disabled={!canResend(r)}
                      onClick={() => onAction({ kind: "RESEND", rows: [r] })}
                    >
                      Reenviar agora
                    </Button>
                    {blocked && <Typography variant="caption" sx={{ alignSelf: "center" }}>{`Reenvio indisponível: ${blocked}`}</Typography>}
                    <Button size="small" disabled={!pending.length} onClick={() => onAction({ kind: "DECIDE", rows: [r] })}>Decidir pelo RH</Button>
                    <Button size="small" disabled={!r.openOccurrenceKeys.length} onClick={() => onAction({ kind: "CLOSE", rows: [r] })}>Encerrar</Button>
                  </Box>
                </TableCell>
              </TableRow>
              {open && (
                <TableRow>
                  <TableCell colSpan={showContract ? 13 : 12}>
                    <Box role="region" aria-label={`Família de ${r.name}`} sx={{ p: 1 }}>
                      <Typography variant="subtitle2">{`Titular: ${decisionText(r.titularDecision)}`}</Typography>
                      {pending.length > 0 && (
                        <Typography variant="body2">{`Falta decidir: ${pending.map((p) => p.name).join(", ")}`}</Typography>
                      )}
                      {r.dependents.length ? (
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Nome</TableCell>
                              <TableCell>CPF</TableCell>
                              <TableCell>Decisão</TableCell>
                              <TableCell>Retido</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {r.dependents.map((d) => (
                              <TableRow key={d.taxPayerId}>
                                <TableCell>{d.name}</TableCell>
                                <TableCell>{cpfMask(d.taxPayerId)}</TableCell>
                                <TableCell>{decisionText(d.decision)}</TableCell>
                                <TableCell>{d.retention ? retentionText(d.retention) : ""}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <Typography variant="body2">Sem dependentes na massa.</Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}
